// src/services/userservice.js
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { authReady } from './auth/tokenCache'; // Caminho conforme sua estrutura

const API_BASE_URL = process.env.REACT_APP_API_SUPORTE_URL;

// Instância base do Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Cria nova config com Authorization: Bearer <ID token>, só dispara quando já existe user
async function withAuthHeader(config, { forceRefresh = false } = {}) {
  await authReady(); // Aguarda auth pronto

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Missing token');

  const idToken = await user.getIdToken(forceRefresh);
  if (!idToken) throw new Error('Missing token');

  const headers = { ...(config.headers || {}), Authorization: `Bearer ${idToken}` };
  return { ...config, headers };
}

// Interceptor de request: injeta token em todas as requisições
api.interceptors.request.use(
  async (config) => withAuthHeader(config),
  (error) => Promise.reject(error)
);

// Interceptor de response: tenta 1 retry em 401 com refresh do token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error?.response?.status;

    if (status === 401 && !original.isRetry) {
      try {
        const retryBase = { ...original, isRetry: true };
        const retryConfig = await withAuthHeader(retryBase, { forceRefresh: true });
        return api.request(retryConfig);
      } catch (_) {
        // fallthrough para o handler abaixo
      }
    }
    return Promise.reject(error);
  }
);

// Handler unificado de erros
const handleAxiosError = (error) => {
  if (error.response) {
    const { status, statusText, data } = error.response;
    const message = data?.error || `Erro ${status}: ${statusText}`;
    throw new Error(message);
  } else if (error.request) {
    throw new Error('Sem resposta do servidor. Verifique sua conexão.');
  } else {
    throw new Error(`Erro: ${error.message}`);
  }
};

// Endpoints

export const getUsers = async () => {
  try {
    const response = await api.get('/');
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const createUser = async (userData) => {
  const tipoNormalizado = String(userData.tipo).toLowerCase();
  const tipoParaEnvio =
    tipoNormalizado === 'administrador' || tipoNormalizado === 'admin'
      ? 'administrador'
      : 'usuario';

  const payload = {
    nome: userData.nome,
    email: userData.email,
    senha: userData.senha,
    tipo: tipoParaEnvio,
  };

  try {
    const response = await api.post('/', payload);
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const updateUser = async (userId, userInfo) => {
  if (!userId) throw new Error('ID do usuário não fornecido para update.');

  let tipoParaEnvio;
  if (typeof userInfo.tipo === 'boolean') {
    tipoParaEnvio = userInfo.tipo ? 'administrador' : 'usuario';
  } else {
    const tipoNormalizado = String(userInfo.tipo).toLowerCase();
    tipoParaEnvio =
      tipoNormalizado === 'administrador' || tipoNormalizado === 'admin'
        ? 'administrador'
        : 'usuario';
  }

  const payload = {
    uid: userId,
    nome: userInfo.nome,
    email: userInfo.email,
    tipo: tipoParaEnvio,
    senha: null,
  };

  try {
    await api.put(`/${encodeURIComponent(userId)}`, payload);
    return { success: true };
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const updateUserPassword = async (userId, newPassword) => {
  if (!userId)
    throw new Error('ID do usuário não fornecido para atualização de senha.');
  if (!newPassword || typeof newPassword !== 'string') {
    throw new Error('A nova senha não é válida.');
  }

  const payload = { uid: userId, senha: newPassword };

  try {
    await api.put(`/${encodeURIComponent(userId)}`, payload);
    return { success: true };
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const deleteUser = async (userId) => {
  if (!userId) throw new Error('ID do usuário não fornecido para deletar.');

  try {
    await api.delete(`/${encodeURIComponent(userId)}`);
    return { success: true };
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};

export const getSupportAgents = async () => {
  try {
    // Usa a instância `api` com interceptor (token automático)
    const response = await api.get('/');
    return response.data;
  } catch (error) {
    handleAxiosError(error);
    return null;
  }
};
